import React, { useEffect, useState } from 'react';
import { getTableList, setUserSettings } from '@/actions';
import CustomCheckbox from '@/components/CustomCheckbox';
import TooltipIcon from '@/components/Tooltip/TooltipIcon';
import { Input } from '@/components/Input';
import IconButton from '@/ctComponents/IconButton';
import { EstimatedSaving } from './estimated-saving';
import LoadingV2 from '@/components/Loading';

export const Preferences = () => {
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [savingData, setSavingData] = useState({
    savingLmcFee: '',
    savingContainerMovingRate: '',
    savingPrePullRate: '',
    savingChassisFee: '',
    savingChassisSplitFee: '',
    savingStorageFee: '',
    savingFuelSurgeCharge: '',
    savingCongestionFee: '',
    savingDetentionFee: '',
    savingScaleFee: '',
    savingFlatFee: '',
  });
  const [formData, setFormData] = useState({
    // Street Turn Empty Availability
    dontShareEmptiesOnOwnChassis: false,
    dontShareEmptiesOnOwnChassisAtCustomerSites: false,
    dontShareEmptiesOfferedForOwnExports: false,
    dontShareEmptiesWithAppointments: false,
    dontShareEmptiesBeforeLFD: false,
    dontShareEmptiesBeforeLFDDays: '',

    // Street Turn Export Availability
    lookForEmptyContainersBeforeShipmentDelivery: false,
    lookForEmptyContainersBeforeShipmentDeliveryDays: '',
    matchExportShipmentsAutomatically: false,

    // Chassis & Flip Service Availability
    flipServiceAvailability: false,
    interestedInRentingChassis: false,
    interestedInRentingChassisDailyRate: '',
  });

  // Load both preferences and saving data
  const loadAllData = () => {
    setLoading(true);
    getTableList('streetTurnPreferencesData')
      .then((res) => {
        if (res.data?.settings?.data) {
          const data = res.data.settings.data;
          setFormData(data);
          setOriginalData(data);

          // Set saving data
          setSavingData({
            savingLmcFee: data.savingLmcFee || '',
            savingContainerMovingRate: data.savingContainerMovingRate || '',
            savingPrePullRate: data.savingPrePullRate || '',
            savingChassisFee: data.savingChassisFee || '',
            savingChassisSplitFee: data.savingChassisSplitFee || '',
            savingStorageFee: data.savingStorageFee || '',
            savingFuelSurgeCharge: data.savingFuelSurgeCharge || '',
            savingCongestionFee: data.savingCongestionFee || '',
            savingDetentionFee: data.savingDetentionFee || '',
            savingScaleFee: data.savingScaleFee || '',
            savingFlatFee: data.savingFlatFee || '',
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading preferences:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Handle checkbox changes
  const handleCheckboxChange = (name) => (event) => {
    const { checked } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked,
    }));
  };

  // Handle input changes
  const handleInputChange = (name) => (event) => {
    const { value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  // Save preferences
  const handleSave = () => {
    const req = {
      key: 'streetTurnPreferencesData',
      data: { ...formData, ...savingData },
    };
    setLoading(true);

    setUserSettings(req)
      .then((res) => {
        setLoading(false);
        // Update original data after successful save
        setOriginalData({ ...formData, ...savingData });
      })
      .catch((err) => {
        console.error('Error saving preferences:', err);
        setLoading(false);
      });
  };

  // Check if data has changed from original
  const hasDataChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  // Update saving data from the EstimatedSaving component
  const updateSavingData = (newSavingData) => {
    setSavingData(newSavingData);
  };

  if (loading) return <LoadingV2 />;

  return (
    <div>
      <div className="lg:grid grid-cols-3 gap-4 xl:gap-8 space-y-4 lg:space-y-0 pb-4">
        <div className="space-y-8 col-span-2">
          {/* Street Turn Empty Availability */}
          <section className="space-y-6">
            <h2 className="text-lg font-bold text-primary-text border-b border-gray-100 pb-2">
              Street Turn Empty Availability
            </h2>

            <div>
              <div className="my-4">
                <div className="flex items-center gap-2">
                  <CustomCheckbox
                    checked={formData.dontShareEmptiesOnOwnChassis}
                    labelClass="text-secondary-text text-sm"
                    onChange={handleCheckboxChange(
                      'dontShareEmptiesOnOwnChassis'
                    )}
                    label="Restrict sharing of containers mounted on your own chassis for street turns"
                  />
                  <TooltipIcon
                    tooltipId={`dont-share-empties-on-own-chassis`}
                    iconId={`dont-share-empties-on-own-chassis`}
                    content="Offer containers mounted on your own chassis for street turns."
                  />
                </div>
              </div>

              <div className="my-4">
                <div className="flex items-start gap-2">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={
                      formData.dontShareEmptiesOnOwnChassisAtCustomerSites
                    }
                    onChange={handleCheckboxChange(
                      'dontShareEmptiesOnOwnChassisAtCustomerSites'
                    )}
                    label="Restrict sharing of containers mounted on your own chassis at customer locations"
                  />
                  <TooltipIcon
                    tooltipId={`dont-share-empties-on-own-chassis-at-customer-sites`}
                    iconId={`dont-share-empties-on-own-chassis-at-customer-sites`}
                    content="Make containers on your own chassis available for street turns at customer locations."
                  />
                </div>
              </div>

              <div className="my-4">
                <div className="flex items-start gap-2">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={formData.dontShareEmptiesOfferedForOwnExports}
                    onChange={handleCheckboxChange(
                      'dontShareEmptiesOfferedForOwnExports'
                    )}
                    label="Restrict offering empty containers needed for your own export shipments. Otherwise, make them available for street turns."
                  />
                  <TooltipIcon
                    tooltipId={`dont-share-empties-offered-for-own-exports`}
                    iconId={`dont-share-empties-offered-for-own-exports`}
                    content="If an empty container is needed for your own export shipment, do not offer it. Otherwise, make it available for street turns."
                  />
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-start gap-2">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={formData.dontShareEmptiesWithAppointments}
                    onChange={handleCheckboxChange(
                      'dontShareEmptiesWithAppointments'
                    )}
                    label="Restrict offering empty containers if they already have scheduled appointments"
                  />
                  <TooltipIcon
                    tooltipId={`dont-share-empties-with-appointments`}
                    iconId={`dont-share-empties-with-appointments`}
                    content="Make empty containers available for street turns, even if they already have appointments."
                  />
                </div>
              </div>

              <div className=" mt-2 mb-4">
                <div className="flex items-center flex-wrap gap-1">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={formData.dontShareEmptiesBeforeLFD}
                    onChange={handleCheckboxChange('dontShareEmptiesBeforeLFD')}
                    label="Restrict sharing empty containers for street turns in the"
                  />
                  <input
                    type="number"
                    value={formData.dontShareEmptiesBeforeLFDDays}
                    onChange={handleInputChange(
                      'dontShareEmptiesBeforeLFDDays'
                    )}
                    className="inline-block h-7 px-1 w-12 mx-1 placeholder-border-secondary text-center border border-solid border-border-tertiary rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    placeholder="Ex. 3"
                  />
                  <span className="text-sm font-medium text-secondary-text">
                    days leading up to their
                  </span>
                  <span className="text-sm font-medium text-secondary-text">
                    Last Free Day (LFD)
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Street Turn Export Availability */}
          <section className="space-y-6">
            <h2 className="text-lg font-bold text-primary-text  border-b border-gray-100 pb-2">
              Street Turn Export Availability
            </h2>

            <div>
              <div className="mt-4 mb-2">
                <div className="flex items-center flex-wrap gap-1">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={
                      formData.lookForEmptyContainersBeforeShipmentDelivery
                    }
                    onChange={handleCheckboxChange(
                      'lookForEmptyContainersBeforeShipmentDelivery'
                    )}
                    label="Search for empty containers for export shipments in the"
                  />
                  <input
                    type="number"
                    value={
                      formData.lookForEmptyContainersBeforeShipmentDeliveryDays
                    }
                    onChange={handleInputChange(
                      'lookForEmptyContainersBeforeShipmentDeliveryDays'
                    )}
                    className="inline-block h-7 px-1 w-12 placeholder-border-secondary py-1 mx-1 text-center border border-solid border-border-tertiary rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                    placeholder="Ex. 3"
                  />
                  <span className="text-sm font-medium text-secondary-text">
                    days leading up to the
                  </span>
                  <span className="text-sm font-medium text-secondary-text">
                    delivery date
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-start flex-nowrap gap-1">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={formData.matchExportShipmentsAutomatically}
                    onChange={handleCheckboxChange(
                      'matchExportShipmentsAutomatically'
                    )}
                    label="Automatically search for empty containers for export shipments when no empties are available within your company for a street turn."
                  />

                  <TooltipIcon
                    tooltipId={`match-export-shipments-automatically`}
                    iconId={`match-export-shipments-automatically`}
                    content="Automatically look for empty containers for export shipments when none are available within your company."
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Chassis & Flip Service Availability */}
          <section className="space-y-6">
            <h2 className="text-lg font-bold text-primary-text  border-b border-gray-100 pb-2">
              Chassis & Flip Service Availability
            </h2>

            <div className="space-y-4">
              <div className="my-4">
                <div className="flex items-start gap-2">
                  <CustomCheckbox
                    labelClass="text-secondary-text text-sm"
                    checked={formData.flipServiceAvailability}
                    onChange={handleCheckboxChange('flipServiceAvailability')}
                    label="Flip Service Availability"
                  />
                  <TooltipIcon
                    tooltipId={`flip-service-availability`}
                    iconId={`flip-service-availability`}
                    content="Offer flip services to transfer containers between chassis for street turn operations."
                  />
                </div>
              </div>

              <div
                className="my-4"
                style={{
                  width: 'fit-content',
                }}>
                <CustomCheckbox
                  labelClass="text-secondary-text text-sm"
                  checked={formData.interestedInRentingChassis}
                  onChange={handleCheckboxChange('interestedInRentingChassis')}
                  label="Interested in Renting Out Own Chassis"
                />
              </div>

              {formData.interestedInRentingChassis && (
                <div className="ml-6 my-4" style={{ maxWidth: '275px' }}>
                  <Input
                    label="Daily Rate"
                    name="interestedInRentingChassisDailyRate"
                    id="interestedInRentingChassisDailyRate"
                    placeholder="Ex. $30 per Day"
                    value={formData.interestedInRentingChassisDailyRate}
                    onChange={(e) =>
                      handleInputChange('interestedInRentingChassisDailyRate')(
                        e
                      )
                    }
                    type="number"
                    className="w-48"
                  />
                </div>
              )}
            </div>
          </section>
        </div>
        <div>
          <EstimatedSaving
            savingData={savingData}
            loading={loading}
            onDataUpdate={loadAllData}
            updateSavingData={updateSavingData}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-solid border-l-0 border-b-0 border-r-0 border-t border-gray-200">
        <IconButton
          loading={loading}
          text={'Save'}
          onClick={!hasDataChanged() ? undefined : handleSave}
          disabled={loading || !hasDataChanged()}
          style={{
            backgroundColor:
              loading || !hasDataChanged() ? '#F5F7FA' : '#0E121B',
            height: '40px',
            width: 165,
            color: loading || !hasDataChanged() ? '#CACFD8' : 'white',
          }}
        />
      </div>
    </div>
  );
};
